// src/App.js
import React from "react";
import {
  Routes,
  Route,
  Navigate,
  BrowserRouter,
  useLocation,
  useNavigationType,
} from "react-router-dom";

import ErrorBoundary from "./components/page_components/ErrorPage";
import RequireAuth from "./gates/RequireAuth";
import SubscribedRoute from "./gates/SubscribedRoute";

import Home from "./components/page_components/home_layout_components/home_page/Home";
import Callback from "./components/page_components/home_layout_components/callback_page/Callback";
import Logout from "./components/page_components/authentication_components/LogOut";
import Faq from "./components/page_components/home_layout_components/faq_page/Faq";
import Pricing from "./components/page_components/home_layout_components/pricing_page/Pricing";
import Contact from "./components/page_components/home_layout_components/contact_page/Contact";
import Dashboard from "./components/page_components/dashboard_layout_components/dashboard_page/Dashboard";
import Layout from "./Layout";
import Cancel from "./components/stripe_components/Cancel";
import Success from "./components/stripe_components/Success";
import DashboardLayout from "./DashboardLayout";
import Subscription from "./components/page_components/dashboard_layout_components/subscription_page/Subscription";
import Profile from "./components/page_components/dashboard_layout_components/Profile";
import Requests from "./components/page_components/dashboard_layout_components/requests_page/Requests";
import Login from "./components/page_components/authentication_components/Login";
import SignUp from "./components/page_components/authentication_components/SignUp";
import Authorization from "./components/page_components/dashboard_layout_components/authorization_page/Authorization";
import PersonalInformationForm from "./components/page_components/dashboard_layout_components/personal_information_page/PersonalInformationForm";
import { UserProvider } from "./context/userProvider";
import NotFound from "./components/page_components/NotFound";
import PreCheckout from "./components/page_components/home_layout_components/precheckout_page/Checkout";
import PrivacyPolicy from "./components/page_components/termly_compliance/PrivacyPolicy";
import CookiePolicy from "./components/page_components/termly_compliance/CookiePolicy";
import TermsOfUse from "./components/page_components/termly_compliance/TermsOfUse";

// ------------------------- Termly Consent Listener -------------------------
let termlyListenerAttached = false;

// ------------------------- Scroll Handling -------------------------
const ScrollToTop = () => {
  const location = useLocation();
  const navigationType = useNavigationType();

  React.useEffect(() => {
    if (navigationType === "PUSH" || navigationType === "REPLACE") {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, navigationType]);

  return null;
};

// ------------------------- Preserve Scroll Router -------------------------
const PreserveScrollRouter = ({ children, future }) => {
  if ("scrollRestoration" in window.history) {
    window.history.scrollRestoration = "auto";
  }

  return <BrowserRouter future={future}>{children}</BrowserRouter>;
};

// ------------------------- Routes Wrapper -------------------------
const AppRoutes = () => {
  const location = useLocation();

  // 🍏 The Production Hard Reflow Fix
  React.useLayoutEffect(() => {
    // 1. Instantly kill standard document active selection node anchors
    if (document.activeElement && document.activeElement !== document.body) {
      document.activeElement.blur();
    }

    // 2. Query all interactive elements across the current DOM viewport
    const interactiveNodes = document.querySelectorAll(
      ".MuiButtonBase-root, .MuiButton-root, button, a, [role='button']",
    );

    interactiveNodes.forEach((node) => {
      node.blur();

      // Explicitly tear out any active material focus states saved in Safari's layout heap
      if (node.classList.contains("Mui-focusVisible")) {
        node.classList.remove("Mui-focusVisible");
      }

      // 3. FORCE iOS Safari to break the visual layer cache by triggering an atomic style reflow
      void node.offsetHeight;
      node.style.webkitTapHighlightColor = "transparent";
    });
  }, [location.pathname]); // Fires on every route delta execution hook

  return (
    <div style={{ height: "100%" }}>
      <Routes>
        {/* Public routes */}
        <Route element={<Layout />}>
          <Route path="/" element={<Home />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/terms-of-use" element={<TermsOfUse />} />
        </Route>

        {/* Protected area */}
        <Route
          element={
            <RequireAuth>
              <SubscribedRoute>
                <DashboardLayout />
              </SubscribedRoute>
            </RequireAuth>
          }
        >
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/requests" element={<Requests />} />

          <Route path="/profile" element={<Profile />}>
            <Route
              index
              element={<Navigate to="personal-information" replace />}
            />
            <Route path="subscription" element={<Subscription />} />
            <Route path="authorization" element={<Authorization />} />
            <Route
              path="personal-information"
              element={<PersonalInformationForm />}
            />
          </Route>
        </Route>

        {/* Standalone pages */}
        <Route
          path="/checkout"
          element={
            <RequireAuth>
              <PreCheckout />
            </RequireAuth>
          }
        />

        <Route path="/callback" element={<Callback />} />
        <Route path="/login" element={<Login />} />
        <Route path="/logout" element={<Logout />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/cancel" element={<Cancel />} />
        <Route
          path="/success"
          element={
            <RequireAuth>
              <Success />
            </RequireAuth>
          }
        />

        {/* Catch-all */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </div>
  );
};

// ------------------------- App -------------------------
function App() {
  // Hard-evict frozen styling properties if the user navigates back via system history swiping
  React.useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        if (
          document.activeElement &&
          document.activeElement !== document.body
        ) {
          document.activeElement.blur();
        }

        const activeElements = document.querySelectorAll(
          ".MuiButtonBase-root, .MuiButton-root, button, a",
        );
        activeElements.forEach((element) => {
          element.blur();
          if (element.classList.contains("Mui-focusVisible")) {
            element.classList.remove("Mui-focusVisible");
          }
          // Read a layout property to smash the browser's hardware visual thread cache
          void element.offsetHeight;
        });
      }
    };

    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  // Termly consent listener
  React.useEffect(() => {
    if (termlyListenerAttached) return;

    const CONSENT_KEY = "termly_previous_consent";

    const handleConsent = (data) => {
      const newConsent = JSON.stringify(data);
      const previousConsent = localStorage.getItem(CONSENT_KEY);

      if (previousConsent !== null && previousConsent !== newConsent) {
        localStorage.setItem(CONSENT_KEY, newConsent);
        return;
      }

      localStorage.setItem(CONSENT_KEY, newConsent);
    };

    const setupConsentListener = () => {
      // Check if consent was already saved
      const savedConsent = localStorage.getItem(CONSENT_KEY);

      if (window.Termly?.getConsentState) {
        const initialConsent = window.Termly.getConsentState();
        localStorage.setItem(CONSENT_KEY, JSON.stringify(initialConsent));
      } else if (savedConsent) {
        // If no current consent but we have saved consent, restore it
        try {
          const consent = JSON.parse(savedConsent);
          if (window.Termly?.setConsentState) {
            window.Termly.setConsentState(consent);
          }
        } catch (e) {
          console.warn("Failed to restore Termly consent:", e);
        }
      }

      if (window.Termly?.on) {
        window.Termly.on("consent", handleConsent);
        termlyListenerAttached = true;
      }
    };

    if (window.Termly?.on) {
      if (window.Termly?.getConsentState) {
        setupConsentListener();
      } else {
        window.Termly.on("initialized", setupConsentListener);
      }
    } else {
      const interval = setInterval(() => {
        if (window.Termly?.on) {
          clearInterval(interval);
          window.Termly.on("initialized", setupConsentListener);
        }
      }, 100);

      return () => clearInterval(interval);
    }
  }, []);

  return (
    <UserProvider>
      <PreserveScrollRouter
        future={{
          v7_relativeSplatPath: true,
          v7_startTransition: true,
        }}
      >
        <ErrorBoundary>
          <ScrollToTop />
          <AppRoutes />
        </ErrorBoundary>
      </PreserveScrollRouter>
    </UserProvider>
  );
}

export default App;
